import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { reaction } from "mobx";
import { useHistory, useLocation } from "react-router-dom";
import agentsPanelStore from "Stores/agentsPanelStore";
import copilotDrawerStore from "Stores/copilotDrawerStore";
import { agentsData } from "Stores/agentsData";
import {
  statusMeta,
  scheduleText,
  BotGlyph
} from "Pages/Dashboard/agents/AgentsList";

// Docked width. Fixed rather than resizable: it is a list, not a workspace.
const PANEL_WIDTH = 360;

// Live first, then the ones that could be switched on; archived never shows.
const ORDER = { ACTIVE: 0, PAUSED: 1, DRAFT: 2 };

/*
 * The agents side panel: every agent the team has, with at a glance which are
 * live and which are not. Opens from the right rail and docks beside the page
 * the same way the copilot drawer does (portalled to <body>, .mainContent gives
 * up the width via a body class + custom property). The two panels share the
 * slot next to the rail, so only one is ever open.
 */
const AgentsPanel = observer(() => {
  const { open } = agentsPanelStore;
  const history = useHistory();
  const location = useLocation();
  // Stays mounted through the slide-out so closing animates like opening.
  const [shown, setShown] = useState(open);
  const onFullPage = location.pathname.startsWith("/copilot");

  useEffect(() => {
    if (open) setShown(true);
  }, [open]);

  // Fresh list on every open: agents change status from elsewhere (runs,
  // approvals, a teammate), and a stale "Live" is worse than a short load.
  useEffect(() => {
    if (open) agentsData.fetchAgents();
  }, [open]);

  // The copilot can open from anywhere (page buttons, the editor wand), not
  // only the rail -- whenever it does, this panel steps aside.
  useEffect(
    () =>
      reaction(
        () => copilotDrawerStore.open,
        copilotOpen => copilotOpen && agentsPanelStore.close()
      ),
    []
  );

  // Dock beside the page, mirroring the copilot's sgCopilotPinned mechanism.
  useEffect(() => {
    const on = open && !onFullPage;
    document.body.classList.toggle("sgAgentsPinned", on);
    document.documentElement.style.setProperty(
      "--sgAgentsPanelWidth",
      on ? `${PANEL_WIDTH}px` : "0px"
    );
    return () => {
      document.body.classList.remove("sgAgentsPinned");
      document.documentElement.style.setProperty("--sgAgentsPanelWidth", "0px");
    };
  }, [open, onFullPage]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => e.key === "Escape" && agentsPanelStore.close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  if (!shown || onFullPage) return null;

  const agents = (agentsData.agents || [])
    .filter(a => a.status !== "ARCHIVED" && a.status !== "DELETED")
    .sort((a, b) => (ORDER[a.status] ?? 3) - (ORDER[b.status] ?? 3));
  const liveCount = agents.filter(a => a.status === "ACTIVE").length;
  const loading = agentsData.agentsLoading && !agents.length;

  const go = path => {
    agentsPanelStore.close();
    history.push(path);
  };

  const hire = async () => {
    if (await agentsData.canHireAgent()) go("/agents/new");
  };

  return ReactDOM.createPortal(
    <aside
      className={`agentsPanel${open ? "" : " closing"}`}
      aria-label="Agents"
      onAnimationEnd={() => !open && setShown(false)}
    >
      <div className="agentsPanel__head">
        <span className="agentsPanel__title">
          <BotGlyph size={18} />
          Agents
        </span>
        {!loading && (
          <span className="agentsPanel__count">
            {liveCount} live · {agents.length} total
          </span>
        )}
        <span className="agentsPanel__spacer" />
        <button
          type="button"
          className="agentsPanel__iconBtn"
          aria-label="Close agents"
          onClick={agentsPanelStore.close}
        >
          <i className="material-icons-outlined">close</i>
        </button>
      </div>

      <div className="agentsPanel__body">
        {loading &&
          [0, 1, 2, 3].map(i => (
            <div key={i} className="agentsPanel__row agentsPanel__row--skeleton linear-background" />
          ))}

        {!loading && !agents.length && (
          <div className="agentsPanel__empty">
            <BotGlyph size={28} />
            <b>No agents yet</b>
            <span>Describe a job in plain English and Salesgear builds the agent.</span>
          </div>
        )}

        {!loading &&
          agents.map(a => {
            const meta = statusMeta[a.status] || statusMeta.DRAFT;
            const live = a.status === "ACTIVE";
            return (
              <button
                type="button"
                key={a.id}
                className="agentsPanel__row"
                onClick={() => go(`/agents/${a.id}`)}
              >
                <span className={`agentsPanel__glyph${live ? " isLive" : ""}`}>
                  <BotGlyph size={18} />
                </span>
                <span className="agentsPanel__info">
                  <span className="agentsPanel__name">{a.name || "Untitled agent"}</span>
                  <span className="agentsPanel__meta">{scheduleText(a)}</span>
                </span>
                <span className={`agentsPanel__pill agentsPanel__pill--${live ? "live" : "off"}`}>
                  {live && <span className="agentsPanel__dot" />}
                  {meta.label}
                </span>
              </button>
            );
          })}
      </div>

      <div className="agentsPanel__foot">
        <button type="button" className="agentsPanel__primary" onClick={hire}>
          <i className="material-icons-outlined">add</i>
          Hire an agent
        </button>
        <button type="button" className="agentsPanel__link" onClick={() => go("/agents")}>
          View all
          <i className="material-icons-outlined">arrow_forward</i>
        </button>
      </div>
    </aside>,
    document.body
  );
});

export default AgentsPanel;
